import React, { useMemo, useState } from "react";
import {
  Anchor,
  Badge,
  Button,
  Container,
  Divider,
  Flex,
  Group,
  Paper,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Sparkles, UsersRound } from "lucide-react";

import "../Login/styles.scss";
import { validateEmail } from "../../../lib/methods";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { NonPaginatedResponse } from "../../../types";
import { DashboardEndpoints } from "../../../lib/endpoints-dashboard";
import { IUserProps } from "../../../types/User";
import { AppName, HttpCodes } from "../../../lib/data";
import { useToastNotification } from "../../../hooks/useToast";

const Register: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToastNotification();
  const navigate = useNavigate();

  const registerForm = useForm({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validate: {
      firstName: (value: string) =>
        value.trim().length > 1 ? null : "First name is required",
      lastName: (value: string) =>
        value.trim().length > 1 ? null : "Last name is required",
      email: (value: string) => (validateEmail(value) ? null : "Invalid email"),
      password: (value: string) => {
        const hasMinLength = value.length >= 8;
        const hasUppercase = /[A-Z]/.test(value);
        const hasLowercase = /[a-z]/.test(value);
        const hasNumber = /\d/.test(value);

        if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber) {
          return "Password must be 8+ chars with upper & lower case letters and a number.";
        }

        return null;
      },
      confirmPassword: (value: string, values) => {
        if (!value) {
          return "Please confirm your password";
        }
        if (value !== values.password) {
          return "Passwords do not match";
        }
        return null;
      },
    },
  });

  const passwordChecks = useMemo(() => {
    const password = registerForm.values.password;
    // Check criteria in fixed order: length, uppercase, lowercase, number
    const checks = [
      password.length >= 8, // Bar 1
      /[A-Z]/.test(password), // Bar 2
      /[a-z]/.test(password), // Bar 3
      /\d/.test(password), // Bar 4
    ];
    return checks;
  }, [registerForm.values.password]);

  const passwordStrength = passwordChecks.filter(Boolean).length;

  const passwordHelpText = useMemo(() => {
    const password = registerForm.values.password;

    // If password is less than 8 characters, show length requirement
    if (password.length < 8) {
      return "At least 8 characters";
    }

    // Once 8+ characters, show what's still missing
    const missingCriteria: string[] = [];

    if (!/[A-Z]/.test(password)) {
      missingCriteria.push("uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      missingCriteria.push("lowercase letter");
    }
    if (!/\d/.test(password)) {
      missingCriteria.push("number");
    }

    if (missingCriteria.length === 0) {
      return "Great! Your password meets all requirements.";
    }

    if (missingCriteria.length === 1) {
      return `Add at least one ${missingCriteria[0]}.`;
    }

    if (missingCriteria.length === 2) {
      return `Add at least one ${missingCriteria[0]} and one ${missingCriteria[1]}.`;
    }

    return `Add at least one ${missingCriteria[0]}, one ${missingCriteria[1]}, and one ${missingCriteria[2]}.`;
  }, [registerForm.values.password]);

  const handleRegister = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { hasErrors } = registerForm.validate();
    if (hasErrors) {
      toast.error("Fix the fields highlighted in red.");
      return;
    }

    const { firstName, lastName, email, password } = registerForm.values;
    toast.info("Creating your workspace...");
    setIsSubmitting(true);

    const response = await PerformRequest<
      NonPaginatedResponse<{
        user: IUserProps;
        access_token: string;
        refresh_token: string;
      }>
    >({
      method: DashboardEndpoints.GetRegister().method,
      route: DashboardEndpoints.GetRegister().route,
      data: {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email,
        password,
      },
      onError: () => setIsSubmitting(false),
      doNotUseAuthorization: true,
    });

    setIsSubmitting(false);

    if (
      response.status === HttpCodes.Success ||
      response.status === HttpCodes.Created
    ) {
      toast.success("Account created! Redirecting to login...");
      setTimeout(() => navigate("/login"), 1200);
      return;
    }

    toast.error(response.message ?? "Unable to create your account right now.");
  };

  return (
    <div className="login-page">
      <div className="login-page__background">
        <span className="glow glow--one" />
        <span className="glow glow--two" />
        <span className="grid-overlay" />
      </div>

      <Container size="xl" className="login-page__container">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          <Stack className="login-page__hero" gap="xl">
            <Badge
              variant="gradient"
              gradient={{ from: "cyan", to: "indigo" }}
              size="lg"
              radius="md"
              leftSection={<Sparkles size={16} />}
            >
              Join the {AppName} movement
            </Badge>

            <div>
              <Title order={1} className="login-page__title">
                Build{" "}
                <Text component="span" inherit className="gradient-text">
                  unforgettable communities
                </Text>
              </Title>
              <Text size="lg" c="dimmed" maw={520}>
                Launch premium experiences, automate onboarding, and sync every
                signal from day-one using our event OS.
              </Text>
            </div>

            <Paper
              radius="md"
              withBorder
              className="login-page__insight"
              p="lg"
            >
              <Flex gap="md" align="flex-start">
                <ThemeIcon variant="light" radius="sm" size="lg" color="cyan">
                  <UsersRound size={18} />
                </ThemeIcon>
                <div>
                  <Text fw={600}>+4k curated communities</Text>
                  <Text size="sm" c="dimmed">
                    Hospitality, tech, sports &amp; culture teams scale faster
                    with {AppName}.
                  </Text>
                </div>
              </Flex>
            </Paper>

            <Group gap="lg">
              <Flex align="center" gap="sm">
                <ThemeIcon variant="white" size="lg" radius="sm" color="green">
                  <ShieldCheck size={18} />
                </ThemeIcon>
                <div>
                  <Text fw={600} size="sm">
                    SOC2-ready infrastructure
                  </Text>
                  <Text size="xs" c="dimmed">
                    SSO, audit trails, and granular roles baked in.
                  </Text>
                </div>
              </Flex>
            </Group>
          </Stack>

          <Paper
            shadow="xl"
            radius="lg"
            p="xl"
            className="login-page__card"
            withBorder
          >
            <Stack gap="md">
              <div>
                <Title order={3}>Create your workspace</Title>
                <Text size="sm" c="dimmed">
                  We just need a few details to spin up your control room.
                </Text>
              </div>

              <form onSubmit={handleRegister}>
                <Stack gap="md">
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    <TextInput
                      label="First name"
                      placeholder="Jackson"
                      size="lg"
                      radius="sm"
                      {...registerForm.getInputProps("firstName")}
                    />
                    <TextInput
                      label="Last name"
                      placeholder="Anthony"
                      size="lg"
                      radius="sm"
                      {...registerForm.getInputProps("lastName")}
                    />
                  </SimpleGrid>

                  <TextInput
                    label="Work email"
                    placeholder="you@company.com"
                    size="lg"
                    radius="sm"
                    {...registerForm.getInputProps("email")}
                  />

                  <div>
                    <PasswordInput
                      label="Password"
                      placeholder="Create a strong password"
                      size="lg"
                      radius="sm"
                      {...registerForm.getInputProps("password")}
                    />
                    <Group gap={6} mt={8}>
                      {[0, 1, 2, 3].map((index) => {
                        // Fill bars from left to right based on total criteria met
                        const shouldFill = passwordStrength > index;
                        // Color bars progressively: red -> orange -> yellow -> green
                        let barColor = "#e5e7eb"; // Default gray
                        if (shouldFill) {
                          if (index === 0) barColor = "#dc2626"; // Red
                          else if (index === 1) barColor = "#ea580c"; // Orange
                          else if (index === 2) barColor = "#facc15"; // Yellow
                          else if (index === 3) barColor = "#22c55e"; // Green
                        }
                        return (
                          <span
                            key={`password-check-${index}`}
                            style={{
                              flex: 1,
                              height: 4,
                              borderRadius: 999,
                              backgroundColor: barColor,
                              transition: "background-color 160ms ease",
                            }}
                          />
                        );
                      })}
                    </Group>
                    <Text
                      size="xs"
                      mt={6}
                      c={
                        passwordStrength === passwordChecks.length
                          ? "teal"
                          : "dimmed"
                      }
                    >
                      {passwordHelpText}
                    </Text>
                  </div>

                  <PasswordInput
                    label="Confirm password"
                    placeholder="Re-enter your password"
                    size="lg"
                    radius="sm"
                    {...registerForm.getInputProps("confirmPassword")}
                  />

                  <Button
                    type="submit"
                    radius="sm"
                    size="lg"
                    className="login-page__primary-button"
                    loading={isSubmitting}
                  >
                    Create account
                  </Button>
                </Stack>
              </form>

              <Text size="sm" c="dimmed" ta="center">
                Already have an account?{" "}
                <Anchor component={Link} to="/login" size="sm" c="cyan">
                  Back to login
                </Anchor>
              </Text>
            </Stack>
          </Paper>
        </SimpleGrid>
      </Container>
    </div>
  );
};

export default Register;
